import React from 'react';
import { Box, Modal, Typography, Stack, Table, TableBody, TableRow, TableCell, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useAgentByIdQuery, useAgentUsageQuery } from '../../API/Agents';

interface Props {
  isOpen: boolean;
  title?: string;
  data: any;
  onClose: () => void;
}

const AgentView = ({ isOpen, title, data, onClose }: Props) => {
  // Fetch detailed agent data by ID if available
  const { data: detailedAgent, isLoading: isLoadingAgent } = useAgentByIdQuery({
    id: data?.id || data?.vapiId,
    config: { enabled: isOpen && !!(data?.id || data?.vapiId) }
  });

  // Fetch agent usage data
  const { data: usageData, isLoading: isLoadingUsage } = useAgentUsageQuery({
    id: data?.id || data?.vapiId,
    days: 30,
    config: { enabled: isOpen && !!(data?.id || data?.vapiId) }
  });

  console.log('AgentView data:', detailedAgent);
  console.log('Usage data:', usageData);

  // Use detailed data if available, otherwise fallback to passed data
  const agentData = detailedAgent?.data || {};
  const usage = usageData?.data || {};
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflow: 'auto',
  };

  const renderValue = (value: any, type?: string) => {
    if (value === null || value === undefined) return '-';
    if (type === 'boolean') return value ? 'Yes' : 'No';
    if (type === 'date') return dayjs(value).format('MM-DD-YYYY, HH:mm A');
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value.toString();
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="agent-view-modal"
      aria-describedby="agent-view-description"
    >
      <Box sx={style}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography id="agent-view-modal" variant="h5" component="h2">
            {title || 'View Agent'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>

        {(isLoadingAgent || isLoadingUsage) && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography>Loading agent details...</Typography>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Basic Information
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Name</TableCell>
                <TableCell>{renderValue(agentData?.name)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell>{renderValue(agentData?.id)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                <TableCell>{renderValue(agentData?.createdAt, 'date')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Updated At</TableCell>
                <TableCell>{renderValue(agentData?.updatedAt, 'date')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell>
                  <span style={{ 
                    color: agentData?.isDeleted ? '#d32f2f' : '#2e7d32',
                    fontWeight: 'bold'
                  }}>
                    {agentData?.isDeleted ? 'Inactive' : 'Active'}
                  </span>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Call Analytics (Last 30 Days)
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Total Calls</TableCell>
                <TableCell>{renderValue(usage?.totalCalls) || '0'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Successful Calls</TableCell>
                <TableCell>{renderValue(usage?.successfulCalls) || '0'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Failed Calls</TableCell>
                <TableCell>{renderValue(usage?.failedCalls) || '0'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Scheduled Calls</TableCell>
                <TableCell>{renderValue(usage?.inProgressCalls) || '0'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Success Rate</TableCell>
                <TableCell>
                  <span style={{ 
                    color: (usage?.successRate || 0) >= 90 ? '#2e7d32' : (usage?.successRate || 0) >= 70 ? '#f57c00' : '#d32f2f',
                    fontWeight: 'bold'
                  }}>
                    {usage?.successRate ? `${usage.successRate}%` : '0%'}
                  </span>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Average Duration</TableCell>
                <TableCell>{usage?.averageDuration ? `${usage.averageDuration} seconds` : '0 seconds'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Duration</TableCell>
                <TableCell>{usage?.totalDuration ? `${Math.floor(usage.totalDuration / 60)} minutes` : '0 minutes'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Used</TableCell>
                <TableCell>{renderValue(usage?.lastUsed, 'date')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {usage?.recentActivity && usage.recentActivity.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Recent Activity
            </Typography>
            <Table>
              <TableBody>
                {usage.recentActivity.map((activity: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                      {dayjs(activity.date).format('MMM DD, YYYY')}
                    </TableCell>
                    <TableCell>
                      {activity.calls} calls (Avg: {Math.round(parseFloat(activity.avgDuration || '0'))} seconds)
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {usage?.callTypes && usage.callTypes.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Call Types
            </Typography>
            <Table>
              <TableBody>
                {usage.callTypes.map((callType: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                      {callType.callType.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}
                    </TableCell>
                    <TableCell>{callType.count} calls</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Configuration
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Model</TableCell>
                <TableCell>{renderValue(agentData?.model?.model)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>First Message</TableCell>
                <TableCell>{renderValue(agentData?.firstMessage)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>End Call Message</TableCell>
                <TableCell>{renderValue(agentData?.endCallMessage)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Background Sound</TableCell>
                <TableCell>{renderValue(agentData?.backgroundSound)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Phone Numbers
          </Typography>
          <Table>
            <TableBody>
              {agentData?.phoneNumbers && agentData.phoneNumbers.length > 0 ? (
                agentData.phoneNumbers.map((phone: any, index: number) => (
                  <TableRow key={phone.id || index}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                      Phone Number {agentData.phoneNumbers.length > 1 ? `${index + 1}` : ''}
                    </TableCell>
                    <TableCell>{renderValue(phone.phoneNumber)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Phone Numbers</TableCell>
                  <TableCell>No phone numbers assigned</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Uploaded Files
          </Typography>
          <Table>
            <TableBody>
              {agentData?.uploadedFiles && agentData.uploadedFiles.length > 0 ? (
                agentData.uploadedFiles.map((file: any, index: number) => (
                  <TableRow key={file.id || index}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                      File {agentData.uploadedFiles.length > 1 ? `${index + 1}` : ''}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {file.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Type: {file.type || 'Unknown'}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          Uploaded: {renderValue(file.uploadedAt, 'date')}
                        </Typography>
                        {file.s3Url && (
                          <Box sx={{ mt: 1 }}>
                            <a 
                              href={file.s3Url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#1976d2', 
                                textDecoration: 'underline',
                                fontSize: '14px'
                              }}
                            >
                              Download File
                            </a>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Uploaded Files</TableCell>
                  <TableCell>No files uploaded</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            System Prompt
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.100', 
            borderRadius: 1, 
            border: '1px solid',
            borderColor: 'grey.300',
            maxHeight: '300px',
            overflow: 'auto'
          }}>
            <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {agentData?.prompt || renderValue(agentData?.model?.messages[0]?.content)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Features
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Allow Inbound</TableCell>
                <TableCell>{renderValue(agentData?.metadata?.isAllowInbound, 'boolean')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Allow Outbound</TableCell>
                <TableCell>{renderValue(agentData?.metadata?.isAllowOutbound, 'boolean')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Send Email</TableCell>
                <TableCell>{renderValue(agentData?.metadata?.isSendEmail, 'boolean')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Book Appointment</TableCell>
                <TableCell>{renderValue(agentData?.metadata?.bookAppointment, 'boolean')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>End Call Function Enabled</TableCell>
                <TableCell>{renderValue(agentData?.endCallFunctionEnabled, 'boolean')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Backchanneling Enabled</TableCell>
                <TableCell>{renderValue(agentData?.backchannelingEnabled, 'boolean')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Voice Configuration
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Voice Provider</TableCell>
                <TableCell>{renderValue(agentData?.voice?.provider)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Voice ID</TableCell>
                <TableCell>{renderValue(agentData?.voice?.voiceId)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Stability</TableCell>
                <TableCell>{renderValue(agentData?.voice?.stability)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Similarity Boost</TableCell>
                <TableCell>{renderValue(agentData?.voice?.similarityBoost)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Transcriber Configuration
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Provider</TableCell>
                <TableCell>{renderValue(agentData?.transcriber?.provider)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                <TableCell>{renderValue(agentData?.transcriber?.model)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Language</TableCell>
                <TableCell>{renderValue(agentData?.transcriber?.language)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Timing Settings
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Max Duration (seconds)</TableCell>
                <TableCell>{renderValue(agentData?.maxDurationSeconds)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Response Delay (seconds)</TableCell>
                <TableCell>{renderValue(agentData?.responseDelaySeconds)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>LLM Request Delay (seconds)</TableCell>
                <TableCell>{renderValue(agentData?.llmRequestDelaySeconds)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Words to Interrupt Assistant</TableCell>
                <TableCell>{renderValue(agentData?.numWordsToInterruptAssistant)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {agentData?.metadata && Object.keys(agentData.metadata).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Metadata
            </Typography>
            <Table>
              <TableBody>
                {Object.entries(agentData.metadata).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>{key}</TableCell>
                    <TableCell>{renderValue(value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default AgentView;